import { ReactElement } from 'react'
import { buildErrorMessage } from '@constants'
import { AxiosError } from 'axios'
import { Button, Stack, Typography } from '@mui/material'
import {
  getGetProjectAiInstructionsQueryKey,
  useCreateProjectAiInstruction,
  useDeleteProjectAiInstruction,
  useGetProjectAiInstructions,
  useSetProjectOrganisationAiInstructionIgnored,
  useUpdateProjectAiInstruction,
} from '@api/ai-instructions/ai-instructions'
import type { ProjectAIInstructionsDTO } from '@api/fastAPI.schemas'
import { useQueryClient } from '@tanstack/react-query'
import { LoadingIndicator } from '@ui/feedback'
import { useSnackbar } from '@ui/snackbar'
import ManagerSection from '../ManagerSection'
import AIInstructionList from './components/AIInstructionList'
import AIInstructionRow from './components/AIInstructionRow'
import useAIInstructionMutationCallbacks from './hooks/useAIInstructionMutationCallbacks'

interface Props {
  projectGuid: string
}

/**
 * KI-Instruktionen in the project Systemmanager: the project's own instructions (independent of
 * the organisation's, nothing is copied) and the organisation's, each of which the project can
 * ignore.
 */
const ProjectAIInstructions = ({ projectGuid }: Props): ReactElement => {
  const { enqueueSnackbar } = useSnackbar()
  const queryClient = useQueryClient()

  const queryKey = getGetProjectAiInstructionsQueryKey(projectGuid)
  const mutationCallbacks = useAIInstructionMutationCallbacks(() =>
    queryClient.invalidateQueries(queryKey),
  )

  const { data, isLoading } = useGetProjectAiInstructions<ProjectAIInstructionsDTO, AxiosError>(
    projectGuid,
    {
      query: {
        onError: error => {
          enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Laden der KI-Instruktionen'), {
            variant: 'error',
          })
        },
      },
    },
  )

  const { mutateAsync: createInstruction, isLoading: isCreating } = useCreateProjectAiInstruction<
    AxiosError,
    unknown
  >({
    mutation: {
      meta: {
        title: 'KI-Instruktion hinzufügen',
        description: 'Fügt dem Projekt eine KI-Instruktion hinzu',
      },
      ...mutationCallbacks(
        'KI-Instruktion hinzugefügt',
        'Fehler beim Speichern der KI-Instruktion',
      ),
    },
  })

  const {
    mutateAsync: updateInstruction,
    isLoading: isUpdating,
    variables: updateVariables,
  } = useUpdateProjectAiInstruction<AxiosError, unknown>({
    mutation: {
      meta: {
        title: 'KI-Instruktion bearbeiten',
        description: 'Ändert eine KI-Instruktion des Projekts',
      },
      ...mutationCallbacks(
        'KI-Instruktion gespeichert',
        'Fehler beim Speichern der KI-Instruktion',
      ),
    },
  })

  const {
    mutate: deleteInstruction,
    isLoading: isDeleting,
    variables: deleteVariables,
  } = useDeleteProjectAiInstruction<AxiosError, unknown>({
    mutation: {
      meta: {
        title: 'KI-Instruktion löschen',
        description: 'Löscht eine KI-Instruktion des Projekts',
      },
      ...mutationCallbacks('KI-Instruktion gelöscht', 'Fehler beim Löschen der KI-Instruktion'),
    },
  })

  const {
    mutate: setIgnored,
    isLoading: isSettingIgnored,
    variables: ignoreVariables,
  } = useSetProjectOrganisationAiInstructionIgnored<AxiosError, unknown>({
    mutation: {
      meta: {
        title: 'KI-Instruktion ignorieren',
        description:
          'Ignoriert eine KI-Instruktion der Organisation in diesem Projekt (oder wendet sie wieder an)',
      },
      onSuccess: updated => {
        queryClient.setQueryData(queryKey, updated)
      },
      onError: error => {
        enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Ändern der KI-Instruktion'), {
          variant: 'error',
        })
      },
    },
  })

  if (isLoading || !data) return <LoadingIndicator />

  return (
    <Stack spacing={3} data-cy="project-ai-instructions">
      <Typography variant="body2" color="text.secondary">
        Instruktionen, die der KI-Assistent (über MCP) bei der Arbeit in diesem Projekt befolgt: die
        Instruktionen der Organisation (außer den ignorierten) und zusätzlich die des Projekts.
      </Typography>

      <ManagerSection title="Projekt-Instruktionen">
        <AIInstructionList
          instructions={data.project_instructions}
          emptyText="Noch keine KI-Instruktionen für dieses Projekt vorhanden."
          isCreating={isCreating}
          updatingId={isUpdating ? updateVariables?.instructionId : undefined}
          deletingId={isDeleting ? deleteVariables?.instructionId : undefined}
          onCreate={text => createInstruction({ projectGuid, data: { text } })}
          onUpdate={(instructionId, text) =>
            updateInstruction({ projectGuid, instructionId, data: { text } })
          }
          onDelete={instructionId => deleteInstruction({ projectGuid, instructionId })}
          addFormId="project-ai-instruction-add"
          data-cy="project-ai-instructions-own"
        />
      </ManagerSection>

      <ManagerSection title="Instruktionen der Organisation">
        <Stack spacing={1} data-cy="project-ai-instructions-organisation">
          {data.organisation_instructions.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              Die Organisation hat keine KI-Instruktionen. Sie werden im globalen Systemmanager
              gepflegt.
            </Typography>
          )}
          {data.organisation_instructions.map(instruction => (
            <AIInstructionRow
              key={instruction.id}
              instruction={instruction}
              ignored={instruction.ignored}
              isUpdating={false}
              isDeleting={false}
              actions={
                <Button
                  size="small"
                  disabled={isSettingIgnored && ignoreVariables?.instructionId === instruction.id}
                  onClick={() =>
                    setIgnored({
                      projectGuid,
                      instructionId: instruction.id,
                      data: { ignored: !instruction.ignored },
                    })
                  }
                  data-cy="ai-instruction-toggle-ignored"
                >
                  {instruction.ignored ? 'Anwenden' : 'Ignorieren'}
                </Button>
              }
            />
          ))}
        </Stack>
      </ManagerSection>
    </Stack>
  )
}

export default ProjectAIInstructions
